import React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
  Box,
  Chip,
  Divider,
  Paper
} from '@mui/material';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import PersonIcon from '@mui/icons-material/Person';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import { handleModeChangeSuggestion } from '../../services/api';

// Map context types to icons and colors
const contextMapping = {
  client_request_human: { 
    icon: <PersonIcon />, 
    label: 'Client requested human', 
    color: 'primary' 
  },
  crisis_situation: { 
    icon: <ErrorIcon />, 
    label: 'Crisis situation', 
    color: 'error' 
  },
  complexity_beyond_ai: { 
    icon: <InfoIcon />, 
    label: 'Complex situation', 
    color: 'info' 
  },
  harmful_content: { 
    icon: <WarningIcon />, 
    label: 'Harmful content', 
    color: 'warning' 
  },
  simple_query: { 
    icon: <SmartToyIcon />, 
    label: 'Simple query', 
    color: 'success' 
  },
  routine_conversation: { 
    icon: <AutorenewIcon />, 
    label: 'Routine conversation', 
    color: 'default' 
  }
};

// Map mode types to icons and colors
const modeMapping = {
  human: { icon: <PersonIcon />, label: 'Human', color: 'primary' },
  ai: { icon: <SmartToyIcon />, label: 'AI', color: 'success' },
  assisted: { icon: <AutorenewIcon />, label: 'Assisted', color: 'info' }
};

// Map urgency levels to icons and colors
const urgencyMapping = {
  low: { icon: <InfoIcon />, label: 'Low', color: 'success' },
  medium: { icon: <InfoIcon />, label: 'Medium', color: 'info' },
  high: { icon: <WarningIcon />, label: 'High', color: 'warning' },
  critical: { icon: <ErrorIcon />, label: 'Critical', color: 'error' }
};

const AiModeChangeSuggestionDialog = ({ 
  open, 
  onClose, 
  suggestion,
  onApprove,
  onReject,
  loading
}) => {
  const handleApprove = async () => {
    if (loading) return;
    
    try {
      const token = localStorage.getItem('token');
      const result = await handleModeChangeSuggestion(
        suggestion.conversationId,
        {
          targetMode: suggestion.suggestedMode,
          reason: suggestion.reason,
          detectedContext: suggestion.detectedContext,
          urgency: suggestion.urgency,
          approved: true
        },
        token
      );
      
      if (onApprove) {
        onApprove(result);
      }
    } catch (error) {
      console.error('Error approving mode change:', error);
    }
  };
  
  const handleReject = async () => {
    if (loading) return;
    
    try {
      const token = localStorage.getItem('token');
      const result = await handleModeChangeSuggestion(
        suggestion.conversationId,
        {
          targetMode: suggestion.suggestedMode,
          reason: suggestion.reason,
          detectedContext: suggestion.detectedContext,
          urgency: suggestion.urgency,
          approved: false
        },
        token
      );
      
      if (onReject) {
        onReject(result);
      }
    } catch (error) {
      console.error('Error rejecting mode change:', error);
    }
  };
  
  if (!suggestion) {
    return null;
  }

  const context = contextMapping[suggestion.detectedContext] || 
    { icon: <InfoIcon />, label: suggestion.detectedContext, color: 'default' };
    
  const mode = modeMapping[suggestion.suggestedMode] || 
    { icon: <AutorenewIcon />, label: suggestion.suggestedMode, color: 'default' };
    
  const urgency = urgencyMapping[suggestion.urgency] || 
    { icon: <InfoIcon />, label: suggestion.urgency, color: 'default' };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="ai-mode-change-dialog-title"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id="ai-mode-change-dialog-title">
        <Box display="flex" alignItems="center">
          <PriorityHighIcon color="warning" sx={{ mr: 1 }} />
          AI Suggested Mode Change
        </Box>
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          The AI has suggested changing the conversation mode for client <strong>{suggestion.clientName}</strong>:
        </DialogContentText>
        
        <Paper elevation={0} variant="outlined" sx={{ p: 2, mt: 2, mb: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            <strong>Current Mode:</strong> {suggestion.currentMode?.toUpperCase() || 'Unknown'}
          </Typography>
          
          <Typography variant="subtitle1" gutterBottom>
            <strong>Suggested Mode:</strong> 
            <Chip 
              icon={mode.icon}
              label={mode.label.toUpperCase()}
              color={mode.color}
              size="small"
              sx={{ ml: 1 }}
            />
          </Typography>
          
          <Typography variant="subtitle1" gutterBottom>
            <strong>Detected Context:</strong> 
            <Chip 
              icon={context.icon}
              label={context.label}
              color={context.color}
              size="small"
              sx={{ ml: 1 }}
            />
          </Typography>
          
          <Typography variant="subtitle1" gutterBottom>
            <strong>Urgency:</strong> 
            <Chip 
              icon={urgency.icon}
              label={urgency.label}
              color={urgency.color}
              size="small"
              sx={{ ml: 1 }}
            />
          </Typography>
        </Paper>
        
        <Divider sx={{ my: 2 }} />
        
        <Typography variant="subtitle1">
          <strong>Reason:</strong>
        </Typography>
        <Paper elevation={0} variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
          <Typography variant="body2">
            {suggestion.reason}
          </Typography>
        </Paper>
        
        <Box sx={{ mt: 3 }}>
          <Typography variant="body2" color="text.secondary">
            If approved, the conversation will automatically switch to {suggestion.suggestedMode.toUpperCase()} mode. 
            If rejected, the conversation will remain in {suggestion.currentMode?.toUpperCase() || 'current'} mode.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button 
          onClick={handleReject} 
          color="error" 
          disabled={loading}
        >
          Reject
        </Button>
        <Button 
          onClick={handleApprove} 
          color="primary" 
          variant="contained" 
          disabled={loading}
        >
          Approve
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AiModeChangeSuggestionDialog;
